import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Admin {
  id: string;
  username: string;
  email: string;
  role: string;
  permissions: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  admin: Admin | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const cachedAdmin = localStorage.getItem('adminData');
      
      if (token) {
        // If we have cached admin data, use it immediately and verify in background
        if (cachedAdmin) {
          try {
            const adminData = JSON.parse(cachedAdmin);
            setIsAuthenticated(true);
            setAdmin(adminData);
            setLoading(false);
            
            // Verify token in background
            verifyTokenInBackground(token);
            return;
          } catch (error) {
            localStorage.removeItem('adminData');
          }
        }
        
        // No cached data, verify token
        await verifyToken(token);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
      setLoading(false);
    }
  };

  const verifyToken = async (token: string) => {
    try {
      const response = await axios.get('https://api.sobcof.com/api/admin/me', {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 5000 // 5 second timeout
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        setAdmin(response.data.data);
        // Cache admin data
        localStorage.setItem('adminData', JSON.stringify(response.data.data));
      } else {
        clearAuthData();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  const verifyTokenInBackground = async (token: string) => {
    try {
      const response = await axios.get('https://api.sobcof.com/api/admin/me', {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 5000
      });

      if (response.data.success) {
        // Update cached data
        localStorage.setItem('adminData', JSON.stringify(response.data.data));
        setAdmin(response.data.data);
      } else {
        clearAuthData();
      }
    } catch (error) {
      console.error('Background token verification failed:', error);
      clearAuthData();
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    sessionStorage.removeItem('backupPin');
    setIsAuthenticated(false);
    setAdmin(null);
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      console.log('🔐 Login attempt started');
      console.log('📝 Credentials:', { username, password });
      console.log('🌐 Request URL:', 'https://api.sobcof.com/api/admin/login');

      const response = await axios.post('https://api.sobcof.com/api/admin/login', {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('✅ Login response received:', response.data);
      console.log('📊 Response status:', response.status);

      if (response.data.success) {
        const { token, admin } = response.data.data;
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminData', JSON.stringify(admin));
        setIsAuthenticated(true);
        setAdmin(admin);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('❌ Login failed:', error);
      console.error('🔍 Error type:', error.name);
      console.error('📄 Error message:', error.message);
      console.error('🌐 Network error:', error.code);
      console.error('📊 Response status:', error.response?.status);
      console.error('📝 Response data:', error.response?.data);
      console.error('🔗 Request config:', error.config?.url);

      if (error.code === 'ECONNREFUSED') {
        console.error('🚫 Backend server is not running!');
      } else if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network connection error!');
      }

      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // For now, just clear local storage
      // In a real app, you'd call the backend logout endpoint
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    admin,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
